import {
  AUTOMATION_FOOD,
  AUTOMATION_HEAL,
  AUTOMATION_HUNT,
  AUTOMATION_MANA,
  AUTOMATION_RECORDING,
  AUTOMATION_RETALIATE,
  MAX_FOOD_FLOOR_MINUTES,
  MAX_HEAL_FLOOR_PERCENT,
  MAX_ROUTES,
  MAX_ROUTE_NAME,
  MAX_WAYPOINTS,
  MIN_FOOD_FLOOR_MINUTES,
  MIN_HEAL_FLOOR_PERCENT,
  RouteCommand,
  type AutomationRequestPacket,
  type AutomationStatePacket,
  type LootFilterPacket,
  type SavedRoute,
  type Waypoint,
} from '@game/protocol';

import { LootFilterPanel } from './loot-filter.ts';
import { collapsiblePanel } from './panel.ts';

/**
 * A tela das automações (PRD §6).
 *
 * ## Por que uma tela só para todas
 *
 * O auto-loot abria sozinho na tecla `L` porque era o único. Com a comida, a cura e a caçada
 * seriam quatro janelas, quatro teclas e quatro lugares para responder à pergunta que o jogador
 * faz ao abrir qualquer uma delas — que não é "o que o auto-loot está pegando?", e sim **"o que
 * este personagem está fazendo sozinho agora?"**.
 *
 * É uma pergunta de segurança, não de conveniência. Quem esquece a auto-caçada ligada perde o
 * personagem numa caverna; quem esquece o revide ligado começa brigas que não escolheu; quem
 * esquece a auto-cura ligada volta do banco com a mochila de poção vazia. Todas precisam ser
 * conferíveis de um relance, e um relance não abre quatro painéis.
 *
 * ## Por que a caçada tem a advertência mais longa da interface
 *
 * Porque ela é a única automação do jogo que **joga**. As outras poupam gestos que o jogador
 * faria de qualquer jeito; esta anda, persegue e mata sem ele. Uma tela que a apresentasse
 * como mais uma caixa de seleção ao lado de "recolher ouro" estaria mentindo sobre o tamanho
 * do que ela liga.
 *
 * ## Por que a rota não é editável aqui
 *
 * Porque ela não é escrita aqui. Quem grava é o servidor, no instante do passo, e o motivo
 * está em `packets/automation.ts`: o cliente nunca vê o personagem parado em cima do degrau da
 * escada — pisar nele é ser movido no mesmo tick —, então uma rota desenhada na tela nunca
 * conteria o único ponto que precisa ser pisado exatamente. O que esta tela tem sobre a rota
 * são os três gestos que não dependem de saber onde os pontos estão: gravar, desfazer o
 * último, apagar tudo.
 *
 * ## Por que a biblioteca fica separada da rota que está na mão
 *
 * Porque são dois objetos com dois donos do gesto. A rota **na mão** é o que está sendo
 * gravado agora e o que a caçada percorre: ela cresce a cada três tiles, tem desfazer, e é
 * descartável por natureza. A **biblioteca** é o que alguém decidiu guardar, e o que guardou
 * não pode mudar sozinho porque o personagem deu mais um passo.
 *
 * Juntá-las — gravar direto por cima do item selecionado — pareceria mais simples e é o
 * desenho que perde rota: um clique errado em "gravar" com "Caverna do Eco" selecionada
 * apagaria meia hora de gravação no primeiro passo, sem confirmação e sem desfazer. Com as
 * duas separadas, o único gesto que escreve na biblioteca é o que se chama "salvar".
 */

export interface AutomationHandlers {
  /** Manda as escolhas ao servidor. Chamado a cada clique. */
  onChange(packet: AutomationRequestPacket): void;
  /** Repassa a lista de auto-loot, que continua tendo pacote próprio. */
  onLootFilterChange(packet: LootFilterPacket): void;
}

export class AutomationPanel {
  private readonly root: HTMLElement;
  private readonly handlers: AutomationHandlers;

  /** O auto-loot continua sendo dono do próprio conteúdo — e do próprio pacote. */
  private readonly lootFilter: LootFilterPanel;

  private readonly foodBox: HTMLInputElement;
  private readonly foodFloor: HTMLSelectElement;
  private readonly foodSummary: HTMLElement;

  private readonly healBox: HTMLInputElement;
  private readonly healFloor: HTMLSelectElement;
  private readonly manaBox: HTMLInputElement;
  private readonly manaFloor: HTMLSelectElement;
  private readonly healSummary: HTMLElement;

  private readonly huntBox: HTMLInputElement;
  private readonly retaliateBox: HTMLInputElement;
  private readonly healthFloor: HTMLSelectElement;
  private readonly recordButton: HTMLButtonElement;
  private readonly dropButton: HTMLButtonElement;
  private readonly clearButton: HTMLButtonElement;
  private readonly routeSummary: HTMLElement;
  private readonly huntSummary: HTMLElement;

  private readonly nameField: HTMLInputElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly libraryList: HTMLElement;
  private readonly librarySummary: HTMLElement;

  private open = false;

  private food = false;
  private hunt = false;
  private recording = false;
  private retaliate = false;
  private heal = false;
  private mana = false;
  private foodFloorMinutes = 5;
  private healthFloorPercent = 30;
  private healFloorPercent = 70;
  private manaFloorPercent = 30;
  private routeRegion = 0;
  private route: Waypoint[] = [];
  private library: SavedRoute[] = [];

  /** Região em que o personagem está agora, para comparar com a da rota. */
  private regionId = 0;
  private regionName = '';

  constructor(root: HTMLElement, handlers: AutomationHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.className = 'automation';

    const header = collapsiblePanel(this.root, 'automacoes', 'Automações');

    // --- Auto-loot: a seção que já existia, inteira ---
    const lootSection = document.createElement('div');
    this.lootFilter = new LootFilterPanel(lootSection, {
      onChange: (packet) => this.handlers.onLootFilterChange(packet),
    });

    // --- Auto-comida ---
    const foodSection = section('autocomida', 'Auto-comida');

    this.foodBox = toggle('Comer sozinho quando ficar com fome', () => {
      this.food = this.foodBox.checked;
      this.commit();
    });

    this.foodFloor = picker(() => {
      this.foodFloorMinutes = Number(this.foodFloor.value);
      this.commit();
    });
    for (let minutes = MIN_FOOD_FLOOR_MINUTES; minutes <= MAX_FOOD_FLOOR_MINUTES; minutes++) {
      this.foodFloor.append(
        option(String(minutes), `comer abaixo de ${minutes} min de saciedade`),
      );
    }

    this.foodSummary = note();

    foodSection.append(
      this.foodBox.parentElement!,
      this.foodFloor,
      this.foodSummary,
    );

    // --- Auto-cura ---
    //
    // Vida e mana têm interruptor **e** piso cada uma, e não um piso comum: os dois números
    // querem valores diferentes por natureza (curar cedo, beber mana tarde), e um controle só
    // obrigaria quem quer os dois a escolher qual dos dois vai ficar errado.
    const healSection = section('autocura', 'Auto-cura');

    this.healBox = toggle('Curar sozinho quando a vida cair', () => {
      this.heal = this.healBox.checked;
      this.commit();
    });

    this.healFloor = healPicker(() => {
      this.healFloorPercent = Number(this.healFloor.value);
      this.commit();
    }, 'curar abaixo de');

    this.manaBox = toggle('Beber poção de mana quando ela cair', () => {
      this.mana = this.manaBox.checked;
      this.commit();
    });

    this.manaFloor = healPicker(() => {
      this.manaFloorPercent = Number(this.manaFloor.value);
      this.commit();
    }, 'beber abaixo de');

    this.healSummary = note();

    healSection.append(
      this.healBox.parentElement!,
      this.healFloor,
      this.manaBox.parentElement!,
      this.manaFloor,
      this.healSummary,
    );

    // --- Auto-caçada ---
    const huntSection = section('autocacada', 'Auto-caçada');

    const warning = document.createElement('p');
    warning.className = 'automation-warning';
    warning.textContent =
      'A caçada anda, escolhe alvos e mata sem você. Ela ataca a criatura hostil mais próxima ' +
      'da rota, persegue quem foge por poucos tiles, e para de andar quando sua vida cai ' +
      'abaixo do piso abaixo. Ela não foge e não volta para a cidade — para beber poção, ' +
      'ligue a auto-cura acima.';

    this.huntBox = toggle('Percorrer a rota gravada', () => {
      this.hunt = this.huntBox.checked;
      this.commit();
    });

    this.retaliateBox = toggle('Revidar sozinho contra quem me atacar', () => {
      this.retaliate = this.retaliateBox.checked;
      this.commit();
    });

    this.healthFloor = picker(() => {
      this.healthFloorPercent = Number(this.healthFloor.value);
      this.commit();
    });
    // "Sem piso" primeiro e **último** na leitura de quem escolhe: é a opção que desliga a
    // única proteção desta tela, e ela precisa estar disponível — quem sabe o que faz às vezes
    // quer isso — sem ser a que se escolhe por descuido de rolagem.
    for (let percent = 10; percent <= 90; percent += 10) {
      this.healthFloor.append(option(String(percent), `parar abaixo de ${percent}% de vida`));
    }
    this.healthFloor.append(option('0', 'sem piso de vida (perigoso)'));

    this.recordButton = button(() => {
      this.recording = !this.recording;
      this.commit();
    });
    this.dropButton = button(() => this.commit(RouteCommand.DropLast));
    this.dropButton.textContent = 'Tirar último ponto';
    this.clearButton = button(() => this.commit(RouteCommand.Clear));
    this.clearButton.textContent = 'Apagar rota';

    const routeRow = document.createElement('div');
    routeRow.className = 'automation-buttons';
    routeRow.append(this.recordButton, this.dropButton, this.clearButton);

    this.routeSummary = note();
    this.huntSummary = note();

    // --- Biblioteca: as rotas guardadas com nome ---
    this.nameField = document.createElement('input');
    this.nameField.type = 'text';
    this.nameField.className = 'automation-name';
    this.nameField.placeholder = 'Caverna do Eco subsolo 1';
    this.nameField.maxLength = MAX_ROUTE_NAME;
    // Mesma trava do `picker`: um campo de texto em foco engole W, A, S e D, e o personagem
    // fica preso enquanto o jogador digita — a prisão que o campo de busca do auto-loot já
    // documenta. Aqui ela seria pior, porque digitar um nome demora mais que escolher um piso.
    this.nameField.addEventListener('keydown', (event) => {
      event.stopPropagation();
      // Enter salva: é o que a mão faz depois de digitar um nome, e sem isto ela vai ao
      // botão para descobrir que o campo já estava certo.
      if (event.key === 'Enter') this.saveCurrentRoute();
    });

    this.saveButton = button(() => this.saveCurrentRoute());
    this.saveButton.textContent = 'Salvar rota';

    const saveRow = document.createElement('div');
    saveRow.className = 'automation-save';
    saveRow.append(this.nameField, this.saveButton);

    this.libraryList = document.createElement('div');
    this.libraryList.className = 'automation-library';

    this.librarySummary = note();

    huntSection.append(
      warning,
      this.huntBox.parentElement!,
      this.retaliateBox.parentElement!,
      this.healthFloor,
      routeRow,
      this.routeSummary,
      this.huntSummary,
      saveRow,
      this.libraryList,
      this.librarySummary,
    );

    this.root.append(header.root, lootSection, foodSection, healSection, huntSection);
    this.render();
  }

  get isOpen(): boolean {
    return this.open;
  }

  /**
   * Abre e fecha. Nenhum campo recebe foco na abertura — ver `LootFilterPanel.releaseFocus`
   * para o defeito que essa regra evita.
   */
  toggle(): boolean {
    this.open = !this.open;
    this.root.classList.toggle('visible', this.open);
    if (!this.open) this.releaseFocus();
    return this.open;
  }

  close(): void {
    this.open = false;
    this.root.classList.toggle('visible', false);
    this.releaseFocus();
  }

  /** A lista de auto-loot que o servidor guarda (PRD §6). Chega uma vez, no `join`. */
  setLootFilter(packet: LootFilterPacket): void {
    this.lootFilter.setFilter(packet);
  }

  /**
   * O estado das automações que o servidor guarda.
   *
   * Chega no `join` e a cada ponto gravado. Não devolve nada ao servidor, pelo mesmo motivo de
   * `LootFilterPanel.setFilter`: responder ao que ele acabou de mandar seria um eco por ponto,
   * e o primeiro que chegasse fora de ordem sobrescreveria um clique recém-dado.
   */
  setState(packet: AutomationStatePacket): void {
    this.food = (packet.flags & AUTOMATION_FOOD) !== 0;
    this.hunt = (packet.flags & AUTOMATION_HUNT) !== 0;
    this.recording = (packet.flags & AUTOMATION_RECORDING) !== 0;
    this.retaliate = (packet.flags & AUTOMATION_RETALIATE) !== 0;
    this.heal = (packet.flags & AUTOMATION_HEAL) !== 0;
    this.mana = (packet.flags & AUTOMATION_MANA) !== 0;
    this.foodFloorMinutes = packet.foodFloorMinutes;
    this.healthFloorPercent = packet.healthFloorPercent;
    this.healFloorPercent = packet.healFloorPercent;
    this.manaFloorPercent = packet.manaFloorPercent;
    this.routeRegion = packet.routeRegion;
    this.route = packet.route;
    this.library = packet.library;

    this.render();
  }

  /**
   * Onde o personagem está agora, vindo do Welcome.
   *
   * A tela precisa disto para uma frase só, e é a frase que evita a pergunta mais confusa que
   * esta feature pode gerar: "liguei a caçada e ele não anda". Uma rota é de um lugar, e
   * atravessar um portal a deixa parada — sem esta comparação, o painel mostraria dezoito
   * pontos e um interruptor ligado sem nada explicando o personagem imóvel.
   */
  setRegion(regionId: number, regionName: string): void {
    this.regionId = regionId;
    this.regionName = regionName;
    this.render();
  }

  private releaseFocus(): void {
    this.lootFilter.releaseFocus();
    this.foodFloor.blur();
    this.healFloor.blur();
    this.manaFloor.blur();
    this.healthFloor.blur();
    this.nameField.blur();
  }

  /**
   * Guarda a rota que está na mão.
   *
   * O slot vai **fora da faixa de propósito** quando o nome digitado não bate com nenhuma
   * gaveta: é o combinado com `applyLibraryCommand`, que responde a isso escolhendo a primeira
   * gaveta livre. Deixar o cliente escolher faria dois cliques rápidos mirarem na mesma gaveta,
   * e o segundo apagaria o primeiro.
   *
   * O nome repetido **substitui**, e é o que faz "salvar de novo depois de estender a volta"
   * funcionar como qualquer pessoa espera: você digita o mesmo nome e a rota daquele nome passa
   * a ser a nova. Sem isto, a segunda gravação da mesma caverna viraria uma segunda gaveta
   * chamada igual, e a lista encheria de homônimos.
   */
  private saveCurrentRoute(): void {
    const name = this.nameField.value.trim();
    const existing = this.library.find(
      (saved) => saved.name.toLowerCase() === name.toLowerCase(),
    );

    this.commit(RouteCommand.Save, existing?.slot ?? MAX_ROUTES, name);
    this.nameField.value = '';
  }

  private commit(
    route: RouteCommand = RouteCommand.Keep,
    slot = 0,
    name = '',
  ): void {
    this.handlers.onChange({
      flags:
        (this.food ? AUTOMATION_FOOD : 0) |
        (this.hunt ? AUTOMATION_HUNT : 0) |
        (this.recording ? AUTOMATION_RECORDING : 0) |
        (this.retaliate ? AUTOMATION_RETALIATE : 0) |
        (this.heal ? AUTOMATION_HEAL : 0) |
        (this.mana ? AUTOMATION_MANA : 0),
      foodFloorMinutes: this.foodFloorMinutes,
      healthFloorPercent: this.healthFloorPercent,
      healFloorPercent: this.healFloorPercent,
      manaFloorPercent: this.manaFloorPercent,
      route,
      slot,
      name,
    });

    // Desenha já com o que foi clicado, sem esperar a volta: o servidor confirma o estado
    // inteiro em seguida (e a rota **só** vem de lá), mas uma caixa que só marca depois do
    // RTT se lê como uma tela que não respondeu ao clique.
    this.render();
  }

  private render(): void {
    this.foodBox.checked = this.food;
    this.foodFloor.value = String(this.foodFloorMinutes);
    this.root.classList.toggle('automation-food-off', !this.food);
    this.foodSummary.textContent = this.food
      ? `Come o maior pedaço que couber, abaixo de ${this.foodFloorMinutes} min.`
      : 'Desligada — a saciedade é por sua conta.';

    this.healBox.checked = this.heal;
    this.healFloor.value = String(this.healFloorPercent);
    this.manaBox.checked = this.mana;
    this.manaFloor.value = String(this.manaFloorPercent);
    // Uma seção, dois interruptores: ela só apaga quando **os dois** estão desligados, senão
    // ligar só a mana deixaria o piso de mana esmaecido junto com o de vida.
    this.root.classList.toggle('automation-heal-off', !this.heal && !this.mana);
    this.healSummary.textContent = this.describeHeal();

    this.huntBox.checked = this.hunt;
    this.healthFloor.value = String(this.healthFloorPercent);

    // A rota **liga o revide sozinha**, e a caixa mostra isso em vez de deixar o jogador
    // descobrir na caverna. A combinação "andando sem revidar" foi medida como defeito: o
    // lobo ataca, o personagem não responde, e o lobo — que ocupa um tile — fecha o corredor.
    // O cavebot fica empurrando contra um bicho que ele se recusa a matar. Ver `retaliate`.
    this.retaliateBox.checked = this.retaliate || this.hunt;
    this.retaliateBox.disabled = this.hunt;
    this.retaliateBox.parentElement?.classList.toggle('forced', this.hunt);
    this.retaliateBox.parentElement?.setAttribute(
      'title',
      this.hunt ? 'A caçada precisa disto: sem revidar, o que te ataca fecha o caminho.' : '',
    );

    this.recordButton.textContent = this.recording ? 'Parar de gravar' : 'Gravar rota';
    this.recordButton.classList.toggle('recording', this.recording);

    // Os dois gestos de edição só existem quando há o que editar. Um botão que não faz nada e
    // não diz por quê é indistinguível de um botão que a tela não entendeu.
    this.dropButton.disabled = this.route.length === 0;
    this.clearButton.disabled = this.route.length === 0;

    this.routeSummary.textContent = this.describeRoute();
    this.huntSummary.textContent = this.describeHunt();

    // Salvar exige o que salvar. Um botão que não faz nada e não diz por quê é indistinguível
    // de um botão que a tela não entendeu — a mesma regra dos dois de edição acima.
    this.saveButton.disabled = this.route.length === 0;
    this.renderLibrary();
  }

  /**
   * Redesenha a lista de rotas guardadas.
   *
   * Reconstrói as linhas inteiras a cada chamada, e é barato porque a lista tem no máximo
   * `MAX_ROUTES` itens: reconciliar dez linhas custaria mais código do que recriá-las, e o
   * código a mais seria justamente o que erra quando uma gaveta some do meio.
   *
   * O que **não** pode ser recriado é o campo de nome: ele fica fora desta função porque quem
   * está digitando um nome perderia o texto e o cursor a cada ponto gravado — a tela redesenha
   * a cada três tiles enquanto a gravação está ligada.
   */
  private renderLibrary(): void {
    this.libraryList.replaceChildren();

    for (const saved of this.library) {
      const row = document.createElement('div');
      row.className = 'automation-route';

      const label = document.createElement('span');
      label.className = 'automation-route-name';
      label.textContent = saved.name;

      const size = document.createElement('span');
      size.className = 'automation-route-size';
      // "de outra região" no lugar da contagem seria esconder o tamanho; junto dela seria uma
      // linha longa demais para a largura do painel. A marca fica no título, que é onde o olho
      // já está — e a linha continua dizendo quantos pontos são.
      size.textContent = counted(saved.points);

      // Uma rota de outra região continua carregável de propósito: é assim que se prepara a
      // caçada **antes** de atravessar o portal. O que ela não pode é parecer pronta para
      // andar aqui, e é isso que a marca diz.
      const elsewhere = saved.region !== 0 && this.regionId !== 0 && saved.region !== this.regionId;
      row.classList.toggle('elsewhere', elsewhere);
      if (elsewhere) label.title = 'Esta rota é de outra região.';

      const load = button(() => this.commit(RouteCommand.Load, saved.slot));
      load.textContent = 'Carregar';

      const rename = button(() => {
        // O nome novo sai do campo de cima quando ele tem texto: é onde a mão já está, e um
        // `prompt()` do navegador travaria o jogo inteiro atrás de uma caixa modal.
        const next = this.nameField.value.trim();
        if (next === '') {
          this.nameField.focus();
          return;
        }
        this.commit(RouteCommand.Rename, saved.slot, next);
        this.nameField.value = '';
      });
      rename.textContent = 'Renomear';

      const remove = button(() => this.commit(RouteCommand.Delete, saved.slot));
      remove.textContent = 'Apagar';
      remove.classList.add('danger');

      const actions = document.createElement('div');
      actions.className = 'automation-route-actions';
      actions.append(load, rename, remove);

      row.append(label, size, actions);
      this.libraryList.append(row);
    }

    this.librarySummary.textContent = this.describeLibrary();
  }

  /**
   * O que a auto-cura vai fazer, em uma frase.
   *
   * A ordem "magia primeiro, poção depois" é dita aqui e não só no servidor porque ela muda o
   * que o jogador deve comprar: quem lê "bebe poção" enche a mochila de poção de vida, e quem
   * lê a frase certa entende que a poção é a reserva de quando a mana acaba.
   *
   * O aviso de "sem piso da caçada" não tem par aqui, e é de propósito: esta automação não pode
   * ser ligada sem piso — a faixa começa em `MIN_HEAL_FLOOR_PERCENT`, e quem não a quer desliga
   * a caixa. Ver o comentário daquela constante.
   */
  private describeHeal(): string {
    if (!this.heal && !this.mana) return 'Desligada — as duas barras são por sua conta.';

    const vida = this.heal
      ? `Cura abaixo de ${this.healFloorPercent}% de vida: conjura o que souber, e bebe poção quando não puder conjurar.`
      : 'A vida é por sua conta.';

    const mana = this.mana
      ? ` Bebe poção de mana abaixo de ${this.manaFloorPercent}%.`
      : '';

    return vida + mana;
  }

  /** Quantas rotas guardadas, e o que fazer com o campo de nome. */
  private describeLibrary(): string {
    if (this.library.length === 0) {
      return this.route.length === 0
        ? 'Nenhuma rota guardada. Grave uma volta e dê um nome a ela.'
        : 'Dê um nome à rota acima para guardá-la.';
    }

    const full = this.library.length >= MAX_ROUTES;
    return full
      ? `Biblioteca cheia (${MAX_ROUTES} rotas). Apague uma para guardar outra.`
      : `${this.library.length} de ${MAX_ROUTES} rotas guardadas. Repetir um nome substitui a rota daquele nome.`;
  }

  /** Quantos pontos, de onde, e em que andares. */
  private describeRoute(): string {
    if (this.recording) {
      return this.route.length === 0
        ? 'Gravando: ande a volta que você quer que ele faça.'
        : `Gravando: ${counted(this.route.length)} até agora (teto de ${MAX_WAYPOINTS}).`;
    }

    if (this.route.length === 0) return 'Nenhuma rota gravada.';

    const floors = [...new Set(this.route.map((point) => point.z))].sort((a, b) => a - b);
    const where =
      floors.length === 1
        ? `andar ${floors[0]}`
        : `andares ${floors[0]}–${floors[floors.length - 1]}`;

    return `${counted(this.route.length)} · ${where}.`;
  }

  /** A frase que diz se a caçada vai de fato andar, e o que a está impedindo. */
  private describeHunt(): string {
    if (!this.hunt) {
      return this.retaliate
        ? 'Caçada desligada — só o revide está ligado.'
        : 'Caçada desligada.';
    }

    if (this.route.length === 0) return 'Ligada, mas sem rota: grave uma volta primeiro.';

    // A comparação que evita "liguei e ele não anda". Ver `setRegion`.
    if (this.routeRegion !== 0 && this.regionId !== 0 && this.routeRegion !== this.regionId) {
      return `A rota é de outra região — parada aqui${this.regionName ? ` em ${this.regionName}` : ''}.`;
    }

    if (this.recording) return 'Ligada, mas parada: ela não anda enquanto você grava.';

    const floor =
      this.healthFloorPercent > 0
        ? `para abaixo de ${this.healthFloorPercent}% de vida`
        : 'sem nenhum piso de vida';

    return `Andando a rota, atacando o que for hostil por perto, e ${floor}.`;
  }
}

function counted(points: number): string {
  return points === 1 ? '1 ponto' : `${points} pontos`;
}

/**
 * Uma seção do painel, com barra de título própria para recolher só ela.
 *
 * A classe com o nome da seção existe para o CSS conseguir mirar **dentro** de uma delas. Sem
 * ela, a régua esmaecida de uma automação desligada era escrita como
 * `.automation-food-off .automation-picker` — e aquele seletor alcança todo seletor do painel,
 * então desligar a auto-comida apagava também o piso de vida da caçada, que não tem nada a ver
 * com ela. Com duas automações usando o mesmo recurso, o alcance demais deixaria de ser um
 * detalhe e viraria uma tela que apaga controles por engano.
 */
function section(id: 'autocomida' | 'autocura' | 'autocacada', label: string): HTMLElement {
  const root = document.createElement('div');
  root.className = `automation-section automation-section-${id}`;

  const header = collapsiblePanel(root, id, label);
  root.append(header.root);
  return root;
}

function note(): HTMLElement {
  const element = document.createElement('p');
  element.className = 'automation-note';
  return element;
}

function button(onClick: () => void): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'automation-button';
  element.addEventListener('click', onClick);
  return element;
}

function picker(onChange: () => void): HTMLSelectElement {
  const element = document.createElement('select');
  element.className = 'automation-picker';
  element.addEventListener('change', onChange);
  // Um `<select>` em foco consome as setas para navegar entre as opções, e as setas são como
  // se anda. Sem isto, escolher um piso e não clicar fora deixa o personagem preso — a mesma
  // prisão que o campo de busca do auto-loot documenta, por um caminho mais silencioso.
  element.addEventListener('keydown', (event) => event.stopPropagation());
  return element;
}

/**
 * Um seletor de piso da auto-cura, de dez em dez por cento.
 *
 * De dez em dez, e não de cinco em cinco: a diferença entre curar a 65% e a 70% não é uma
 * decisão que alguém tome, e dobrar a lista dobraria a rolagem para escolher o mesmo valor.
 * A faixa vem do protocolo, então mudá-la lá muda esta lista sem ninguém voltar aqui.
 */
function healPicker(onChange: () => void, verb: string): HTMLSelectElement {
  const element = picker(onChange);
  for (
    let percent = MIN_HEAL_FLOOR_PERCENT;
    percent <= MAX_HEAL_FLOOR_PERCENT;
    percent += 10
  ) {
    element.append(option(String(percent), `${verb} ${percent}%`));
  }
  return element;
}

function option(value: string, label: string): HTMLOptionElement {
  const element = document.createElement('option');
  element.value = value;
  element.textContent = label;
  return element;
}

function toggle(label: string, onChange: () => void): HTMLInputElement {
  const line = document.createElement('label');
  line.className = 'lootfilter-toggle';

  const box = document.createElement('input');
  box.type = 'checkbox';
  box.addEventListener('change', onChange);

  const text = document.createElement('span');
  text.textContent = label;

  line.append(box, text);
  return box;
}
